import PropTypes from 'prop-types'
import { Table } from 'baseui/table-semantic'
import { SIZE } from 'baseui/input'
import SDButton from '@/shared/components/SDComponents/SDButton/SDButton'
import { KIND, SIZE as BUTTON_SIZE } from 'baseui/button'
import { useTranslation } from 'react-i18next'

const AdditionalProductsList = ({ items, onSort, sortKey, sortDirection, onDelete, onRestore, loadingId }) => {
  const { t } = useTranslation()

  const columnsMeta = [
    { key: 'id', label: t('additionalProductsManagement:table.id') },
    { key: 'type', label: t('additionalProductsManagement:table.type') },
    { key: 'name', label: t('additionalProductsManagement:table.name') },
    { key: 'potential_name', label: t('additionalProductsManagement:table.potential_name') },
    { key: 'country', label: t('additionalProductsManagement:table.country') },
    { key: 'business_type', label: t('additionalProductsManagement:table.business_type') },
    { key: 'zoho_id', label: t('additionalProductsManagement:table.zoho_id') },
    { key: 'attributes', label: t('additionalProductsManagement:table.attributes') },
    { key: 'group', label: t('additionalProductsManagement:table.group') },
    { key: 'order', label: t('additionalProductsManagement:table.order') },
    { key: 'is_active', label: t('additionalProductsManagement:table.is_active') },
    { key: 'deleted_at', label: t('additionalProductsManagement:table.deleted_at') },
    { key: 'action', label: t('additionalProductsManagement:table.action') },
  ]

  const COLUMNS = columnsMeta.map(col => {
    if (col.key === 'action') return col.label
    const isSorted = sortKey === col.key
    const arrow = isSorted ? (sortDirection === 'ASC' ? ' ▲' : ' ▼') : ''
    return (
      <button
        key={col.key}
        onClick={() => onSort(col.key)}
        style={{
          cursor: 'pointer',
          background: 'none',
          border: 'none',
          fontWeight: isSorted ? 600 : 500,
          fontSize: '0.875rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.375rem',
          padding: 0,
          color: isSorted ? '#276EF1' : '#171A1C',
          transition: 'color 0.2s ease',
        }}
        onMouseEnter={e => {
          if (!isSorted) e.target.style.color = '#276EF1'
        }}
        onMouseLeave={e => {
          if (!isSorted) e.target.style.color = '#171A1C'
        }}
      >
        <span>
          {col.label}
          {isSorted && <span style={{ fontSize: '0.75rem' }}>{arrow}</span>}
        </span>
      </button>
    )
  })

  const renderAttributes = value => {
    if (value == null) return '-'
    if (typeof value === 'string') return value
    try {
      return JSON.stringify(value)
    .replace(/[{}"]/g, '')
      .split(',')
      .map(pair => pair.trim())
      .join('\n')
    } catch {
      return '-'
    }
  }

  const data = items.map(p => [
    p.id,
    p.type,
    p.name ? <span className='product-element'>{p.name}</span> : '',
    p.potential_name,
    p.country || '-',
    p.business_type || '-',
    p.zoho_id || '-',
    renderAttributes(p.attributes),
    p.group || '-',
    p.order || '-',
    <span key={`active-state-${p.id}`} className='active-element'>{p.is_active ? t('additionalProductsManagement:table.yes') : t('additionalProductsManagement:table.no')}</span>,
    <span key={`deleted-state-${p.id}`} className='deleted-element'>{p.deleted_at ? new Date(p.deleted_at).toLocaleString() : '-'}</span>,
    p.deleted_at ? (
      <SDButton
        key={`restore-${p.id}`}
        size={BUTTON_SIZE.mini}
        kind={KIND.secondary}
        onClick={() => onRestore(p)}
        isLoading={loadingId === p.id}
      >
        {t('additionalProductsManagement:actions.restore')}
      </SDButton>
    ) : (
      <SDButton
        key={`delete-${p.id}`}
        size={BUTTON_SIZE.mini}
        kind={KIND.secondary}
        onClick={() => onDelete(p)}
        isLoading={loadingId === p.id}
      >
        {t('additionalProductsManagement:actions.delete')}
      </SDButton>
    ),
  ])

  const overrides = {
    TableHeadCell: {
      style: {
        backgroundColor: '#F7F9FC',
        color: '#171A1C',
        fontWeight: 600,
        fontSize: '0.8125rem',
        textTransform: 'uppercase',
        letterSpacing: '0.03125rem',
        padding: '1rem 0.75rem',
        borderBottom: '0.125rem solid #E2E8F0',
      },
    },
    TableBodyCell: {
      style: {
        fontSize: '0.875rem',
        padding: '0.875rem 0.75rem',
      },
    },
    TableBodyRow: {
      style: ({ $theme, $rowIndex }) => {
        const deleted = items[$rowIndex]?.deleted_at
        const zebra = $rowIndex % 2 ? '#FFFFFF' : '#F9FAFB'
        const deletedColor = '#FEF2F2'
        return {
          backgroundColor: deleted ? deletedColor : zebra,
          ':hover': {
            backgroundColor: deleted ? '#FEE2E2' : '#F0F4F8',
            transform: 'scale(1.001)',
            boxShadow: '0 0.0625rem 0.1875rem rgba(0,0,0,0.05)',
          },
          transition: 'all 0.2s ease',
          borderBottom: '0.0625rem solid #E5E7EB',
        }
      },
    },
  }

  return (
    <div data-testid='products-table'>
      <Table
        overrides={overrides}
        size={SIZE.compact}
        columns={COLUMNS}
        data={data}
        emptyMessage={<h1>{t('additionalProductsManagement:no_results')}</h1>}
      />
    </div>
  )
}

AdditionalProductsList.propTypes = {
  items: PropTypes.array.isRequired,
  onSort: PropTypes.func.isRequired,
  sortKey: PropTypes.string.isRequired,
  sortDirection: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  loadingId: PropTypes.number,
}

export default AdditionalProductsList
